import React, { useState } from 'react';
import { Icon } from '@amzn/orion-components';
import { useControls } from 'src/api/hooks/useControlData';
import type { ControlMetadata } from '@amzn/controllershiphub-typescript-client';
import { useNavigate, useLocation } from 'react-router-dom';
import { SECTION_CONFIG } from 'src/constants/sectionConfig';
import { ROUTES } from 'src/router/routes';

const slugify = (str: string) => str.replace(/\s/g, '-').toLowerCase();

/**
 * Resolve the destination path for a control's sidenav sub-item.
 * Procurement is a bespoke page (/procurement), so it is special-cased to stay
 * consistent with the landing-page card (which also routes to ROUTES.PROCUREMENT.BASE).
 */
const controlHref = (c: ControlMetadata): string =>
  c.controlId?.toLowerCase().includes('procurement')
    ? ROUTES.PROCUREMENT.BASE
    : `/${slugify(c.controlType!)}/${c.controlId}`;

export default function OrionSidenav() {
  const { data } = useControls();
  const controls = data?.controls ?? [];
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  return (
    <nav
      className={`landing-sidenav ${expanded ? 'landing-sidenav--expanded' : ''}`}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setExpanded(false);
        }
      }}
    >
      <button
        className="landing-sidenav__toggle"
        aria-expanded={expanded}
        aria-label="Toggle navigation"
        onClick={() => setExpanded((prev) => !prev)}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '8px',
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <Icon name="menu" size="sm" />
      </button>
      {SECTION_CONFIG.map(({ icon, headerText, controlType }, idx) => {
        const sectionControls = controls.filter(
          (c: ControlMetadata) => c.controlType === controlType
        );
        return (
          <div key={headerText} className="landing-sidenav__section">
            <a
              href={`/#${headerText.replace(/\s/g, '-').toLowerCase()}`}
              className={`landing-sidenav__item ${location.hash === `#${slugify(headerText)}` ? 'landing-sidenav__item--active' : ''}`}
              title={headerText}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/#${headerText.replace(/\s/g, '-').toLowerCase()}`);
              }}
            >
              <Icon name={icon} size="sm" color={idx === 0 ? '#0891B2' : '#9ca3af'} />
              {expanded && <span className="landing-sidenav__label">{headerText}</span>}
            </a>
            {expanded &&
              sectionControls.map((c) => (
                <a
                  key={c.controlId}
                  className="landing-sidenav__subitem"
                  href={controlHref(c)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(controlHref(c));
                  }}
                >
                  {c.controlName}
                </a>
              ))}
          </div>
        );
      })}
    </nav>
  );
}
